import { createApp, reactive, watch } from "vue";
import App from "./App.vue";
import router, { checkMaintenance } from "@/router/index";
import i18n, { loadLocaleMessages } from "./i18n";
import store from "@/utils/store";
import imageHelper from "@/utils/imageHelper";
import consoleController from "@/utils/logUtil";
import constant from "@/const";

const app = createApp(App);

/**
 * ハッシュモードのURLからクエリパラメータを取得
 */
function getQueryParamsFromHash() {
  const { hash } = window.location; // #/path?param=value

  // クエリパラメータ部分を抽出
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) {
    return new URLSearchParams();
  }

  const queryString = hash.substring(queryIndex + 1);
  return new URLSearchParams(queryString);
}

/**
 * アプリ起動
 */
async function initializeApp() {
  const hashParams = getQueryParamsFromHash();
  // URLのクエリパラメータから言語を初期化
  const country = hashParams.get("country");
  const lang = hashParams.get("lang");
  store.initLocaleFromQuery(country, lang);

  // 指定された言語ファイルを読み込み
  try {
    await loadLocaleMessages(store.currentLocale.value);
    consoleController("log", `Initial language: ${store.currentLocale.value}`);
  } catch (error) {
    consoleController(
      "error",
      `failed to load initial language: ${store.currentLocale.value}`,
    );
    consoleController("error", error);
  }
  // vue-i18nとstore.jsを同期
  i18n.global.locale = store.currentLocale.value;

  // 言語変更を監視してvue-i18nと同期
  watch(store.currentLocale, async (newLocale) => {
    consoleController("log", `locale changed: ${newLocale}`);
    // 新しい言語ファイルを読み込み
    await loadLocaleMessages(newLocale);

    // i18nのロケールを変更
    i18n.global.locale = newLocale;

    // URLパラメータを変更
    const currentRoute = router.currentRoute.value;
    const newQuery = { ...currentRoute.query };
    // 地域コードがデフォルト値の場合、クエリから取り除く
    if (newLocale === constant.DEFAULT_LOCALE) {
      delete newQuery.lang;
    }
    // URLに含める言語コード
    const urlLang = store.getUrlLang(newLocale);
    if (urlLang !== "") {
      newQuery.lang = urlLang;
    }
    // 言語が変わっていたら実行
    if (currentRoute.query.lang !== newQuery.lang) {
      await router.replace({
        path: currentRoute.path,
        query: newQuery,
      });
    }
  });

  // ========================================
  // グローバルプロパティとして画像関数を登録
  // ========================================

  /**
   * 共通画像を取得
   * 使用例: this.$getCommonImage('log', 'svg')
   */
  app.config.globalProperties.$getCommonImage = (imageName, ext = "png") =>
    imageHelper.getCommonImage(imageName, ext);

  /**
   * 多言語画像を非同期で取得してコンポーネント内に保存
   * 使用例: await this.$loadI18nImage('main', 'png')
   */
  app.config.globalProperties.$loadI18nImage = function $loadI18nImage(
    imageName,
    locale = null,
    ext = "png",
  ) {
    const rawUrl = imageHelper.getLocalizedImageAsync(
      imageName,
      locale || store.currentLocale.value,
      ext,
    );

    let cleanUrl = rawUrl || ""; // -> ./img/xxx.png

    if (
      cleanUrl &&
      !cleanUrl.startsWith("data:") &&
      !cleanUrl.startsWith("http")
    ) {
      const { origin } = window.location; // -> https://aaa.jp

      // BASE_URL (vue.config.js の publicPath 経由で VUE_APP_SUB_PATH が反映された値)
      let baseUrl = process.env.BASE_URL || "/";
      if (!baseUrl.startsWith("/")) baseUrl = `/${baseUrl}`;
      if (!baseUrl.endsWith("/")) baseUrl = `${baseUrl}/`; // -> /bbb/

      // 先頭の"./","/"を削除 -> img/xxx.png
      let path = cleanUrl.replace(/^(\.\/|\/)/, "");

      // 3. rawUrl にすでに subPath ("bbb/") が含まれていれば削って二重化防止
      const subPathDir = baseUrl.replace(/^\/|\/$/g, ""); // -> bbb
      if (subPathDir && path.startsWith(`${subPathDir}/`)) {
        path = path.substring(subPathDir.length + 1); // pathがbbb/img/xxx.png であれば img/xxx.pngを取り出す
      }

      // ドメイン + サブパス + 画像パス で完全なURLを作成
      cleanUrl = `${origin}${baseUrl}${path}`; // -> https://aaa.jp/bbb/img/xxx.png
    }

    const key = `i18n_${imageName}_${ext}`;

    if (this) {
      // コンポーネント内に保管先オブジェクトがない場合は作成
      if (!this.$i18nImages) {
        this.$i18nImages = reactive({});
      }
      this.$i18nImages[key] = cleanUrl;
    }

    return cleanUrl;
  };

  /**
   * 読み込み済みの多言語画像を取得
   * 使用例: this.$getI18nImage('main', 'png')
   */
  app.config.globalProperties.$getI18nImage = function $getI18nImage(
    imageName,
    ext = "png",
  ) {
    const key = `i18n_${imageName}_${ext}`;
    return this.$i18nImages ? this.$i18nImages[key] || "" : "";
  };

  /**
   * 現在の地域コードを取得
   * 使用例: this.$currentLocale
   */
  Object.defineProperty(app.config.globalProperties, "$currentLocale", {
    get() {
      return store.currentLocale.value;
    },
  });

  // URLに含める言語コード
  const urlLang = store.getUrlLang(store.currentLocale.value);
  await checkMaintenance(urlLang);

  app.use(router);
  app.use(i18n);

  // Vue Routerの準備が整うのを待つ
  await router.isReady();

  // URLのcountryとlangを判定結果と同期する
  const newQuery = { ...router.currentRoute.value.query };

  // 国コードがデフォルト値の場合、クエリから取り除く
  if (store.currentCountry === constant.DEFAULT_COUNTRY) {
    delete newQuery.country;
  } else {
    newQuery.country = store.currentCountry.value;
  }
  // 地域コードがデフォルト値の場合、クエリから取り除く
  if (store.currentLocale.value === constant.DEFAULT_LOCALE) {
    delete newQuery.lang;
  }
  // URLに含める言語コード
  if (urlLang !== "") {
    newQuery.lang = urlLang;
  }

  await router.replace({
    path: router.currentRoute.value.path,
    query: newQuery,
  });

  app.mount("#app");
}

// アプリケーション初期化
initializeApp();
